import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";
import FilterBar, { type FilterState } from "./components/FilterBar";

const PAGE_SIZE = 6;

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCat = searchParams.get("cat") || "";
  const filters: FilterState = {
    styles: searchParams.getAll("style"),
    colors: searchParams.getAll("color"),
    seasons: searchParams.getAll("season"),
    sort: searchParams.get("sort") || "date",
  };
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const currentCategory = categories.find((c) => c.slug === activeCat);

  useDocumentMeta({
    title: currentCategory
      ? `黑丝 | ${currentCategory.name}分类 - 黑丝时尚搭配站`
      : "黑丝 | 分类列表 - 黑丝时尚搭配站",
    description: currentCategory
      ? `${currentCategory.description} 浏览黑丝${currentCategory.name}分类下的精选搭配指南与潮流资讯。`
      : "浏览黑丝网站的全部搭配指南、丝袜穿搭技巧与潮流资讯分类，找到属于你的时尚灵感。",
    keywords: `黑丝,${currentCategory ? currentCategory.name : "分类"},丝袜搭配,时尚穿搭,潮流资讯`,
  });

  const filtered = useMemo(() => {
    const list = articles.filter((item) => {
      if (activeCat && item.category !== activeCat) return false;
      if (filters.styles.length && !filters.styles.includes(item.style)) return false;
      if (filters.colors.length && !filters.colors.includes(item.color)) return false;
      if (filters.seasons.length && !filters.seasons.includes(item.season)) return false;
      return true;
    });
    return list.sort((a, b) =>
      filters.sort === "popular" ? b.popularity - a.popularity : a.date < b.date ? 1 : -1
    );
  }, [activeCat, filters.styles, filters.colors, filters.seasons, filters.sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateParams = (next: Record<string, string | string[] | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      params.delete(key);
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, v));
      } else if (value) {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  };

  const handleFilterChange = (next: Partial<FilterState>) => {
    const payload: Record<string, string | string[]> = {};
    if (next.styles) payload.style = next.styles;
    if (next.colors) payload.color = next.colors;
    if (next.seasons) payload.season = next.seasons;
    if (next.sort) payload.sort = next.sort;
    updateParams({ ...payload, page: null });
  };

  const handleReset = () => {
    const params = new URLSearchParams();
    if (activeCat) params.set("cat", activeCat);
    setSearchParams(params);
  };

  const breadcrumbItems = currentCategory
    ? [{ label: "分类列表", to: "/categories" }, { label: currentCategory.name }]
    : [{ label: "分类列表" }];

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-24 md:px-8 md:pt-28">
      <Breadcrumb items={breadcrumbItems} />

      <header className="mt-6 border-b border-foreground-200/70 pb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">Categories</p>
        <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          {currentCategory ? currentCategory.name : "全部内容分类"}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {currentCategory
            ? currentCategory.description
            : "汇集黑丝搭配、丝袜穿搭技巧与潮流资讯三大板块，按风格、颜色与季节自由筛选，找到最适合你的那份灵感。"}
        </p>
      </header>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => updateParams({ cat: null, page: null })}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
            !activeCat ? "bg-foreground-950 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
          }`}
        >
          全部
        </button>
        {categories.map((cat) => (
          <button
            key={cat.slug}
            type="button"
            onClick={() => updateParams({ cat: cat.slug, page: null })}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              activeCat === cat.slug
                ? "bg-foreground-950 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <FilterBar
          filters={filters}
          resultCount={filtered.length}
          onChange={handleFilterChange}
          onReset={handleReset}
        />
      </div>

      <p className="mt-6 text-sm text-foreground-500">
        共找到 <strong className="text-foreground-900">{filtered.length}</strong> 篇内容
      </p>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onChange={(p) => {
              updateParams({ page: String(p) });
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-foreground-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-search-eye-line" />
          </span>
          <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">暂无符合条件的搭配内容</h3>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            试试调整筛选条件，或恢复默认状态浏览全部搭配指南。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> 重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}